import { useEffect, useState } from "react";
import { Alert, Button, Form, Input, Modal, Typography } from "antd";
import type { AccountCommandError, LifecycleActionKind } from "./userAccountAdminApi";

const ACTION_TITLE: Readonly<Record<LifecycleActionKind, string>> = {
  activate: "Kích hoạt tài khoản",
  lock: "Khóa tài khoản",
  unlock: "Mở khóa tài khoản",
};

const ACTION_OK: Readonly<Record<LifecycleActionKind, string>> = {
  activate: "Kích hoạt",
  lock: "Khóa tài khoản",
  unlock: "Mở khóa",
};

const ACTION_IMPACT: Readonly<Record<LifecycleActionKind, (name: string) => string>> = {
  activate: (name) => `Tài khoản của ${name} sẽ được kích hoạt để tham gia hoạt động.`,
  lock: (name) => `Khóa tài khoản của ${name} sẽ chặn các tác vụ mới nhưng vẫn giữ nguyên hồ sơ và lịch sử.`,
  unlock: (name) => `Mở khóa tài khoản của ${name}; các quyền đã hết hiệu lực sẽ không được khôi phục.`,
};

function needsReason(action: LifecycleActionKind): boolean {
  return action !== "activate";
}

export function UserAccountLifecycleModal({
  open,
  action,
  accountName,
  submitting,
  error,
  onCancel,
  onSubmit,
  onReloadConflict,
}: {
  readonly open: boolean;
  readonly action: LifecycleActionKind;
  readonly accountName: string;
  readonly submitting: boolean;
  readonly error: AccountCommandError | null;
  readonly onCancel: () => void;
  readonly onSubmit: (reason: string | undefined) => void;
  readonly onReloadConflict: () => Promise<void>;
}) {
  const [form] = Form.useForm<{ reason?: string | undefined }>();
  const [reloadingConflict, setReloadingConflict] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleReload = async () => {
    setReloadingConflict(true);
    await onReloadConflict().catch((reloadError: unknown) => void reloadError);
    setReloadingConflict(false);
  };

  const handleSubmit = async () => {
    let values: { reason?: string | undefined };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const reason = values.reason?.trim();
    onSubmit(needsReason(action) ? (reason ?? "") : undefined);
  };

  const conflictAlert = error ? (
    error.code === "COMMON_VERSION_CONFLICT" ? (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        title="Dữ liệu đã thay đổi"
        description={error.requestId ? `Mã hỗ trợ: ${error.requestId}` : undefined}
        action={
          <Button size="small" loading={reloadingConflict} onClick={() => void handleReload()}>
            Tải lại thông tin
          </Button>
        }
      />
    ) : (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        title="Không thể thực hiện thao tác"
        description={error.requestId ? `Mã hỗ trợ: ${error.requestId}` : undefined}
      />
    )
  ) : null;

  return (
    <Modal
      open={open}
      title={ACTION_TITLE[action]}
      okText={ACTION_OK[action]}
      cancelText="Hủy"
      okButtonProps={{ disabled: submitting || reloadingConflict, loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      mask={{ closable: false }}
      onCancel={onCancel}
      onOk={() => void handleSubmit()}
    >
      {conflictAlert}
      <Typography.Paragraph type="secondary">{ACTION_IMPACT[action](accountName)}</Typography.Paragraph>
      <Form form={form} layout="vertical" preserve={false}>
        {needsReason(action) && (
          <Form.Item
            name="reason"
            label="Lý do"
            rules={[
              { required: true, whitespace: true, message: "Bắt buộc nhập lý do" },
              { max: 500, message: "Lý do tối đa 500 ký tự" },
            ]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
